import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isPlainClick, prefersReducedMotion } from './motion';

// elebyte.io's signature control, ported from Experience/src/ui/prism/PrismButton.jsx so Support speaks
// the same language:
//   hover/focus → springy 1.06 scale; the italic-900 label blurs out and the icon pops in
//   click       → 14 skewed strips cascade across the pill (18ms stagger), reset after 900ms
// Hover/focus is pure CSS (`.prism-btn` in styles/index.css); only the click cascade is React state.
//
// morph="swap"  — the icon replaces the label (the menu pill)
// morph="nudge" — the label stays readable and the icon springs in beside it (calls to action)
//
// Where it goes:
//   to      — an in-app route (react-router). Navigation waits ACTIVATE_MS so the cascade is seen to start.
//   href    — any other URL, same wait.
//   onClick — runs at once (a form action shouldn't lag); the cascade plays over whatever it does.
//   type="submit" — submits its form at once.
// Modified clicks (Cmd/Ctrl/Shift/middle) on a link are left to the browser. Reduced motion: no wait.

export const STRIPS = 14;
export const RESET_MS = 900;
export const ACTIVATE_MS = 120;

export interface PrismButtonProps {
  label: ReactNode;
  icon?: ReactNode;
  variant?: 'cyan' | 'ghost';
  morph?: 'swap' | 'nudge';
  iconPosition?: 'start' | 'end';
  size?: 'sm' | 'lg';
  block?: boolean;
  to?: string;
  href?: string;
  external?: boolean;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  pressed?: boolean;
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
  id?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-haspopup'?: 'dialog' | 'menu' | boolean;
}

// Only a route link needs the router: a plain button works anywhere, even outside a <Router>.
export default function PrismButton(props: PrismButtonProps) {
  return props.to ? <PrismRouteLink {...props} /> : <PrismCore {...props} />;
}

function PrismRouteLink(props: PrismButtonProps) {
  const navigate = useNavigate();
  const { to } = props;
  return <PrismCore {...props} go={() => { if (to) navigate(to); }} />;
}

function PrismCore({
  label,
  icon,
  variant = 'cyan',
  morph = 'nudge',
  iconPosition = 'end',
  size,
  block = false,
  to,
  href,
  external = false,
  onClick,
  type = 'button',
  disabled = false,
  pressed = false,
  ariaLabel,
  className = '',
  style,
  go,
  ...rest
}: PrismButtonProps & { go?: () => void }) {
  const [clicked, setClicked] = useState(false);
  const resetRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    clearTimeout(resetRef.current);
    if (pendingRef.current) clearTimeout(pendingRef.current);
  }, []);

  const cascade = () => {
    // Reduced motion has no strips (styles/index.css), so no clicked state: its colours assume them.
    if (prefersReducedMotion()) return;
    setClicked(true);
    clearTimeout(resetRef.current);
    resetRef.current = setTimeout(() => setClicked(false), RESET_MS);
  };

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    const isLink = Boolean(to || href);
    if (isLink && (!isPlainClick(e) || external)) return; // the browser's way: new tab, or a new-tab link
    cascade();
    if (!isLink) {
      onClick?.(e);
      return;
    }
    e.preventDefault();
    onClick?.(e);
    // One navigation per cascade: rapid repeat clicks replay the strips but don't double-fire.
    if (pendingRef.current) return;
    const leave = () => {
      pendingRef.current = null;
      if (to) go?.();
      else if (href) window.location.assign(href);
    };
    if (prefersReducedMotion()) leave();
    else pendingRef.current = setTimeout(leave, ACTIVATE_MS);
  };

  const classes = [
    'prism-btn',
    `prism-btn--${variant}`,
    `prism-btn--${morph}`,
    iconPosition === 'start' && 'prism-btn--icon-start',
    size && `prism-btn--${size}`,
    block && 'prism-btn--block',
    clicked && 'is-clicked',
    pressed && 'is-pressed',
    className,
  ].filter(Boolean).join(' ');

  const iconEl = icon ? <span className="prism-btn__icon" aria-hidden="true">{icon}</span> : null;
  const inner = (
    <>
      {Array.from({ length: STRIPS }, (_, i) => (
        <span key={i} className="prism-btn__strip" style={{ '--i': i } as CSSProperties} aria-hidden="true" />
      ))}
      {iconPosition === 'start' && iconEl}
      <span className="prism-btn__label">{label}</span>
      {iconPosition !== 'start' && iconEl}
    </>
  );

  const common = { ...rest, className: classes, style, 'aria-label': ariaLabel, onClick: handleClick };

  if (to) {
    return (
      <Link {...common} to={to} aria-disabled={disabled || undefined}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        {...common}
        href={href}
        aria-disabled={disabled || undefined}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
      >
        {inner}
      </a>
    );
  }
  return (
    // eslint-disable-next-line react/button-has-type
    <button {...common} type={type} disabled={disabled}>
      {inner}
    </button>
  );
}
